package dev.lingvo.shared.screenshots.render

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.SizeRule
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.frames.FramePaintOptions
import dev.lingvo.shared.screenshots.geom.Rect

internal fun paintDevice(layer: DeviceLayer, rect: Rect, image: ImageHandle?, session: RenderSession, shadow: DeviceShadow?) {
    if (image == null && layer.screen is ImageSource.Capture && session.inputs.mode == RenderMode.EXPORT) return
    val aspect = image?.let { it.height.toDouble() / it.width } ?: canonicalAspect(session)
    val layout = session.frames.layout(layer.frame, rect, aspect, layer.screenRadius)
    val options = FramePaintOptions(layer.finish, session.design.palette.bg, layer.homeIndicator, layer.glare, shadow)
    val placeholder = image == null && layer.screen is ImageSource.Capture && session.inputs.mode == RenderMode.EDITOR
    var paintedPlaceholder = false
    // Null screens are filled black by contract. Insert the placeholder there so hardware overlays stay on top.
    val surface = if (placeholder) object : Surface by session.surface {
        override fun fill(path: Path, paint: ResolvedPaint, rule: FillRule) {
            session.surface.fill(path, paint, rule)
            if (path == layout.screenPath && paint == ResolvedPaint.Solid(0xFF000000)) {
                paintMissingCapture(session.surface, layout.screenPath, layout.screen)
                paintedPlaceholder = true
            }
        }
    } else session.surface
    session.frames.paint(surface, layer.frame, layout, options, image)
    // Minimal injected frames may leave null screens untouched instead of emitting a screen fill.
    if (placeholder && !paintedPlaceholder) paintMissingCapture(session.surface, layout.screenPath, layout.screen)
}

private fun canonicalAspect(session: RenderSession): Double {
    val rule = DeviceClass.fromId(session.board.deviceClass)?.spec?.captureRule
    val size = when (rule) {
        is SizeRule.Exact -> rule.sizes.firstOrNull()
        is SizeRule.Range -> rule.aspects.firstOrNull()
        null -> null
    }
    return size?.let { it.height.toDouble() / it.width } ?: (session.board.canvas.height.toDouble() / session.board.canvas.width)
}
